import * as Constants from '~/utils/Constants'
import * as OWUtils from '~/utils/OWUtils'
import { ServerApi } from '~/utils/ServerApi'
import { EmployeeSearchIndex } from '~/models/EmployeeSearchIndex'
import { OWAuthDataModel } from '~/models/OWAuthDataModel'
import * as EmployeeUtils from '~/utils/EmployeeUtils'
import { Employee, Id } from '~/app/types'
import { Global } from '~/global'

export class EmployeesModel {
    static _instance?: EmployeesModel

    private prefetched: boolean
    private readonly employees: { [id: Id]: Employee }
    private readonly freshInserts: { [id: Id]: boolean }
    private readonly searchIndex: EmployeeSearchIndex

    constructor() {
        this.prefetched = false

        // The actual cache map; id(Number) -> employee(Employee)
        this.employees = {}

        // Tracks inserts from this browser session, to avoid flushing fresh data; id(Number) -> true
        this.freshInserts = {}

        this.searchIndex = EmployeeSearchIndex.getInstance()

        // Preload cache with prefetch API
        if (!OWUtils.hasFeature(Constants.Features.DISABLE_PRELOADING)) {
            this.prefetchEmployees()
        }
    }

    static getInstance() {
        if (!this._instance) {
            this._instance = new EmployeesModel()
        }
        return this._instance
    }

    /**
     * Populate the cache by fetching employees from the prefetch endpoint
     */
    prefetchEmployees() {
        if (this.prefetched || !Global.OW_AUTH_DATA.employee?.id) {
            return
        }

        void ServerApi.prefetchEmployees()
            .then(() => (this.prefetched = true))
            .catch(err => console.error('prefetchEmployees failed', err))
    }

    /**
     * Insert employees into the cache
     * @param employees The employees
     * @param [stale] If present, the inserted data is stale
     */
    _insertEmployees(employees: Employee[], stale = false) {
        const authEmployee = OWAuthDataModel.getAuthEmployee()
        employees.map(e => {
            if (!OWAuthDataModel.isLurking() && authEmployee && e.lineage) {
                EmployeeUtils.addProximity(authEmployee, [e])
            }

            // Merge on existing
            const existing = this._getEmployee(e.id)
            const merged = Object.assign({}, existing, e)
            this.employees[e.id] = merged
            if (!stale) {
                this.freshInserts[e.id] = true
            }

            if (e.is_active) {
                this.searchIndex.insertEmployee(merged)
            }
            return merged
        })
    }

    /**
     * Insert an employee into the cache
     * @param e The employee
     */
    static insertEmployee(e: Employee) {
        EmployeesModel.getInstance()._insertEmployees([e])
    }

    /**
     * Get an employee from the cache
     * @param id The id of the employee
     * @returns The employee, or undefined if they are not in the cache
     */
    _getEmployee(id: Id): Employee | undefined {
        return this.employees[id]
    }

    /**
     * Get an employee from the cache
     * @param id The id of the employee
     * @returns The employee, or undefined if they are not in the cache
     */
    static getEmployee(id: Id): Employee | undefined {
        return EmployeesModel.getInstance()._getEmployee(id)
    }

    /**
     * Get a manager id from the cache that is a valid for a Manager Feedback survey
     * @param email The email of the employee
     * @returns The employee id, or undefined if they are not in the cache
     */
    _getValidManagerForFeedbackSurvey(email: string): Id | undefined {
        // Valid manager is active and has at least 3 total reports at time of survey creation
        const employee = Object.values(this.employees).find(
            e => e.email === email && e.is_active && (e.direct_reports_count ?? 0) >= 3
        )
        return employee?.id
    }

    /**
     * Get a manager from the cache that is a valid for a Manager Feedback survey
     * @param email The email of the employee
     * @returns The employee id, or undefined if they are not in the cache
     */
    static getValidManagerForFeedbackSurvey(email: string): Id | undefined {
        return EmployeesModel.getInstance()._getValidManagerForFeedbackSurvey(email)
    }

    /**
     * Search the cached employees
     * @param  query The search query
     * @returns  The cached employees that match the query
     */
    searchEmployees(query: string): (Employee | undefined)[] {
        return this.searchIndex.search(query).map(({ id }) => this._getEmployee(id))
    }

    /**
     * Get an employee from the server if it is not already in the cache
     * @param id The id of the employee
     * @param includeInactive Whether to return inactive employees from the server
     * @returns The employee, or undefined if they are not in the cache
     */

    static async tryGetEmployee(id: Id, includeInactive: boolean = false): Promise<Employee | undefined> {
        const employee = EmployeesModel.getInstance()._getEmployee(id)
        if (employee) return employee

        return ServerApi.getEmployeeById(id, includeInactive)
    }
}
